import { HashtagSourcesApiResponse } from 'api/api.types';
import {
  color,
  Flex,
  PrimaryText,
  spacing,
  StyleProps,
  typography,
  useColorMode,
  useIsMobileView,
} from 'deepstash-ui';
import React, { useMemo } from 'react';
import NextImage from 'src/components/image/NextImage';
import { normalizeHashtagSources } from 'utils/normalizers/hashtag';
import commonStrings from 'utils/strings/commonStrings';
import pageStrings from 'utils/strings/pageStrings';
import { METATOPICS, UNSORTED_TOPICS } from 'utils/metatopics.constants';

interface TopicHeaderV2Props {
  hashtagSourcesApiResponse: HashtagSourcesApiResponse;
  pageNumber?: string;
}

const TopicHeaderV2: React.FC<TopicHeaderV2Props & StyleProps> = ({
  hashtagSourcesApiResponse,
  pageNumber,
  ...props
}) => {
  const hashtagSources = normalizeHashtagSources(hashtagSourcesApiResponse);
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();

  const { titleStyle, subTitleStyle } = useMemo(() => {
    if (isMobileView)
      return {
        titleStyle: typography.primaryHeadingFonts.small.bold,
        subTitleStyle: typography.primaryTextFonts.medium.regular,
      };
    return {
      titleStyle: typography.primaryHeadingFonts.large.bold,
      subTitleStyle: typography.primaryTextFonts.medium.regular,
    };
  }, [isMobileView]);

  const headerImage = useMemo(() => {
    // The topic names are different in METATOPICS, so we have to remove the spaces and switch to lower case
    const possibleImageUrls = METATOPICS.filter(
      topic => topic.exploreId === `hashtag-${hashtagSources.hashtag}`,
    ).map(topic => topic.image);

    // We return a component only if we have a topic image
    if (possibleImageUrls.length > 0) {
      const imageUrl = possibleImageUrls[0];
      return (
        <NextImage
          wrapperStyle={{
            height: spacing.toRem(64),
            minWidth: spacing.toRem(64),
            mr: spacing.M.rem,
          }}
          imageUrl={imageUrl}
          alt={hashtagSources.hashtag}
          priority={true}
          isNotInteractable={true}
        />
      );
    }

    return null;
  }, [hashtagSources.hashtag]);

  return (
    <Flex width="100%" align="center" height="100%" {...props}>
      {/**
       * TODO Fix Yourself source image viewport
       */}
      {/* <Box width="112px" ml="-24px" mr="-8px">
        <YourselfBackground
          backgroundType={(hashtagSources.hashtag.length % 6) + 1}
          height="64px"
          width="112px"
        />
      </Box> */}
      {headerImage}
      <Flex flexDir="column">
        <Flex>
          <PrimaryText {...titleStyle} color={color[colorMode].text}>
            {UNSORTED_TOPICS[hashtagSources.hashtag]?.title ??
              hashtagSources.hashtag}
            {pageNumber && ` - Page ${pageNumber}`}
          </PrimaryText>
        </Flex>
        {hashtagSources.totalIdeas && (
          <PrimaryText
            {...subTitleStyle}
            mr={spacing.XXS.rem}
            color={color[colorMode].textSecondary}
          >
            <b>{hashtagSources.totalIdeas}</b>{' '}
            <b>{commonStrings.ideas.toLowerCase()}</b>{' '}
            {pageStrings.topic.header.onThisTopic}
          </PrimaryText>
        )}
      </Flex>
    </Flex>
  );
};

export default TopicHeaderV2;
